/**
 * Node Configuration Panel
 * Comprehensive configuration panel for workflow nodes
 */
import React, { useState, useEffect } from 'react';
import { Node } from 'reactflow';
import {
  X,
  ChevronDown,
  ChevronRight,
  Trash2,
  Copy,
  Info,
  Plus,
  Settings,
  Wrench,
  Layers,
  Maximize2,
} from 'lucide-react';
import styles from './NodeConfigPanel.module.css';

interface NodeConfigPanelProps {
  node: Node | null;
  onUpdate: (nodeId: string, data: any) => void;
  onDelete: (nodeId: string) => void;
  onDuplicate: (nodeId: string) => void;
  onClose: () => void;
  assistants?: Array<{ id: string; name: string }>;
  tools?: Array<{ id: string; name: string; category: string }>;
}

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({
  node,
  onUpdate,
  onDelete,
  onDuplicate,
  onClose,
  assistants = [],
  tools = [],
}) => {
  // Section expand states
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    nodeSettings: true,
    assistantConfig: true,
    toolConfig: true,
    stateSettings: true,
    transitionSettings: false,
    retryPolicy: false,
  });

  // Tool selection modal
  const [showToolModal, setShowToolModal] = useState(false);

  if (!node) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <h3>Node configuration</h3>
          <div className={styles.headerActions}>
            <button className={styles.iconButton} onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>Select a node to configure</p>
        </div>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateNodeData = (key: string, value: any) => {
    onUpdate(node.id, { ...node.data, [key]: value });
  };

  const updateNestedData = (parent: string, key: string, value: any) => {
    onUpdate(node.id, {
      ...node.data,
      [parent]: { ...(node.data[parent] || {}), [key]: value },
    });
  };

  // Render description based on node type
  const getNodeDescription = () => {
    switch (node.type) {
      case 'iterator':
        return 'Iterates over a collection, allowing the workflow to perform actions for each item in the collection.';
      case 'conditional':
        return 'Decides the next state based on a condition evaluated at runtime.';
      case 'switch':
        return 'Allows for multiple branching paths based on different conditions, similar to a switch-case statement in programming.';
      case 'assistant':
        return 'Executes an AI assistant to process data and generate responses based on the configured task.';
      case 'tool':
        return 'Executes a specific tool or MCP tool to perform actions like API calls, data processing, or integrations.';
      case 'custom':
        return 'Executes custom code or logic defined by the user.';
      case 'transform':
        return 'Transforms data from one format to another using defined mappings.';
      case 'note':
        return 'A documentation note that does not affect workflow execution.';
      default:
        return '';
    }
  };

  // Render configuration based on node type
  const renderNodeConfig = () => {
    switch (node.type) {
      case 'iterator':
        return renderIteratorConfig();
      case 'conditional':
        return renderConditionalConfig();
      case 'switch':
        return renderSwitchConfig();
      case 'assistant':
        return renderAssistantConfig();
      case 'tool':
        return renderToolConfig();
      case 'custom':
      case 'transform':
        return renderCustomConfig();
      case 'note':
        return renderNoteConfig();
      default:
        return renderBasicConfig();
    }
  };

  // Iterator configuration
  const renderIteratorConfig = () => (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('nodeSettings')}>
          <ChevronDown size={16} className={expandedSections.nodeSettings ? '' : styles.collapsed} />
          <span>NODE SETTINGS</span>
        </div>
        {expandedSections.nodeSettings && (
          <div className={styles.sectionContent}>
            <div className={styles.field}>
              <label>
                Iteration Key <span className={styles.required}>*</span>
                <button className={styles.infoButton}>
                  <Info size={14} />
                </button>
              </label>
              <input
                type="text"
                value={node.data.iterKey || ''}
                onChange={(e) => updateNodeData('iterKey', e.target.value)}
                placeholder="e.g., files"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Conditional configuration
  const renderConditionalConfig = () => (
    <>
      <div className={styles.field}>
        <label>Expression</label>
        <input
          type="text"
          value={node.data.expression || ''}
          onChange={(e) => updateNodeData('expression', e.target.value)}
          placeholder='e.g., result.status = "success"'
        />
      </div>
      <div className={styles.note}>
        <Info size={14} />
        <span>NOTE: Expression is evaluated as a Python-like boolean expression using fields from output_schema</span>
      </div>
    </>
  );

  // Switch configuration
  const renderSwitchConfig = () => {
    const cases = node.data.cases || [{ expression: '' }, { expression: '' }];

    const addCase = () => {
      updateNodeData('cases', [...cases, { expression: '' }]);
    };

    const updateCase = (index: number, value: string) => {
      const newCases = [...cases];
      newCases[index] = { expression: value };
      updateNodeData('cases', newCases);
    };

    const removeCase = (index: number) => {
      updateNodeData('cases', cases.filter((_: any, i: number) => i !== index));
    };

    return (
      <>
        {cases.map((c: any, index: number) => (
          <div key={index} className={styles.caseField}>
            <label>Case {index + 1}</label>
            <div className={styles.caseInput}>
              <input
                type="text"
                value={c.expression}
                onChange={(e) => updateCase(index, e.target.value)}
                placeholder='e.g., status = "success"'
              />
              <button className={styles.deleteIconButton} onClick={() => removeCase(index)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        <button className={styles.addButton} onClick={addCase}>
          <Plus size={14} />
          Add
        </button>
        <div className={styles.note}>
          <Info size={14} />
          <span>NOTE: Cases are evaluated as Python-like boolean expressions using fields from output_schema</span>
        </div>
      </>
    );
  };

  // Assistant configuration
  const renderAssistantConfig = () => (
    <>
      {/* Assistant Configuration Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('assistantConfig')}>
          <ChevronDown size={16} className={expandedSections.assistantConfig ? '' : styles.collapsed} />
          <span>ASSISTANT CONFIGURATION</span>
        </div>
        {expandedSections.assistantConfig && (
          <div className={styles.sectionContent}>
            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.useVirtualAssistant ?? true}
                  onChange={(e) => updateNodeData('useVirtualAssistant', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Use Virtual Assistant</span>
            </div>

            <div className={styles.field}>
              <label>Select assistant:</label>
              <select
                value={node.data.assistantId || ''}
                onChange={(e) => updateNodeData('assistantId', e.target.value)}
              >
                <option value="">Select an assistant</option>
                {assistants.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {node.data.assistantId && !assistants.find(a => a.id === node.data.assistantId) && (
                <span className={styles.errorText}>Selected assistant was not found</span>
              )}
            </div>

            <div className={styles.field}>
              <label>Limit Tool Output Tokens:</label>
              <input
                type="number"
                value={node.data.limitToolOutputTokens || ''}
                onChange={(e) => updateNodeData('limitToolOutputTokens', e.target.value)}
                placeholder="Enter limit"
              />
            </div>
          </div>
        )}
      </div>

      {/* State Settings Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('stateSettings')}>
          <ChevronDown size={16} className={expandedSections.stateSettings ? '' : styles.collapsed} />
          <span>STATE SETTINGS</span>
        </div>
        {expandedSections.stateSettings && (
          <div className={styles.sectionContent}>
            <div className={styles.field}>
              <label>
                State ID <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={node.data.stateId || node.id}
                onChange={(e) => updateNodeData('stateId', e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.fieldHeader}>
                <label>Task</label>
                <button className={styles.expandButton}>
                  <Maximize2 size={14} />
                  Expand
                </button>
              </div>
              <textarea
                value={node.data.task || ''}
                onChange={(e) => updateNodeData('task', e.target.value)}
                placeholder="Describe the task for this node"
                rows={4}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.fieldHeader}>
                <label>Output Schema</label>
                <button className={styles.expandButton}>
                  <Maximize2 size={14} />
                  Expand
                </button>
              </div>
              <textarea
                value={node.data.outputSchema || ''}
                onChange={(e) => updateNodeData('outputSchema', e.target.value)}
                placeholder="Expected output format (JSON Schema)"
                rows={3}
              />
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.interruptBefore ?? false}
                  onChange={(e) => updateNodeData('interruptBefore', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Interrupt Before</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.finishIteration ?? false}
                  onChange={(e) => updateNodeData('finishIteration', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Finish Iteration</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.resolveDynamicValues ?? false}
                  onChange={(e) => updateNodeData('resolveDynamicValues', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Resolve Dynamic Values in Prompt</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.resultAsHumanMessage ?? false}
                  onChange={(e) => updateNodeData('resultAsHumanMessage', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Result as Human Message</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Transition Settings Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('transitionSettings')}>
          {expandedSections.transitionSettings ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span>TRANSITION SETTINGS</span>
        </div>
        {expandedSections.transitionSettings && (
          <div className={styles.sectionContent}>
            <div className={styles.field}>
              <label>
                Output Key
                <button className={styles.infoButton}><Info size={14} /></button>
              </label>
              <input
                type="text"
                value={node.data.outputKey || ''}
                onChange={(e) => updateNodeData('outputKey', e.target.value)}
                placeholder="e.g.: analysis_result"
              />
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.keepHistory ?? true}
                  onChange={(e) => updateNodeData('keepHistory', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Keep History</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.overrideTask ?? false}
                  onChange={(e) => updateNodeData('overrideTask', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Override Task</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.storeInContext ?? true}
                  onChange={(e) => updateNodeData('storeInContext', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Store in Context</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.clearPriorMessages ?? false}
                  onChange={(e) => updateNodeData('clearPriorMessages', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Clear Prior Messages</span>
              <button className={styles.infoButton}><Info size={14} /></button>
            </div>

            <div className={styles.field}>
              <label>
                Clear Context Store
                <button className={styles.infoButton}><Info size={14} /></button>
              </label>
              <select
                value={node.data.clearContextStore || 'no'}
                onChange={(e) => updateNodeData('clearContextStore', e.target.value)}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            <div className={styles.field}>
              <label>
                Reset Context Keys
                <button className={styles.infoButton}><Info size={14} /></button>
              </label>
              <input
                type="text"
                value={node.data.resetContextKeys || ''}
                onChange={(e) => updateNodeData('resetContextKeys', e.target.value)}
                placeholder="e.g.: user_data,analysis_result"
              />
            </div>
          </div>
        )}
      </div>

      {/* Retry Policy Section */}
      {renderRetryPolicySection()}
    </>
  );

  // Tool configuration
  const renderToolConfig = () => (
    <>
      {/* Tool Configuration Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('toolConfig')}>
          <ChevronDown size={16} className={expandedSections.toolConfig ? '' : styles.collapsed} />
          <span>TOOL CONFIGURATION</span>
        </div>
        {expandedSections.toolConfig && (
          <div className={styles.sectionContent}>
            <button className={styles.selectButton} onClick={() => setShowToolModal(true)}>
              <Settings size={16} />
              Select tool
            </button>
            <button className={styles.selectButton} onClick={() => setShowToolModal(true)}>
              <Layers size={16} />
              Select MCP tool
            </button>

            {node.data.selectedTool && (
              <div className={styles.selectedTool}>
                <span>Selected: {node.data.selectedTool.name}</span>
              </div>
            )}

            <div className={styles.field}>
              <label>Tool Result JSON Pointer:</label>
              <input
                type="text"
                value={node.data.toolResultPointer || ''}
                onChange={(e) => updateNodeData('toolResultPointer', e.target.value)}
                placeholder="path"
              />
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.enableTracing ?? false}
                  onChange={(e) => updateNodeData('enableTracing', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Enable Tracing</span>
            </div>

            <div className={styles.toggleField}>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={node.data.resolveDynamicValuesInResponse ?? false}
                  onChange={(e) => updateNodeData('resolveDynamicValuesInResponse', e.target.checked)}
                />
                <span className={styles.toggleSlider}></span>
              </label>
              <span>Resolve Dynamic Values in Response</span>
            </div>
          </div>
        )}
      </div>

      {/* State Settings */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('stateSettings')}>
          <ChevronDown size={16} className={expandedSections.stateSettings ? '' : styles.collapsed} />
          <span>STATE SETTINGS</span>
        </div>
        {expandedSections.stateSettings && (
          <div className={styles.sectionContent}>
            <div className={styles.field}>
              <label>
                State ID <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={node.data.stateId || node.id}
                onChange={(e) => updateNodeData('stateId', e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <div className={styles.fieldHeader}>
                <label>Task</label>
                <button className={styles.expandButton}>
                  <Maximize2 size={14} />
                  Expand
                </button>
              </div>
              <textarea
                value={node.data.task || ''}
                onChange={(e) => updateNodeData('task', e.target.value)}
                placeholder="Describe the task for this node"
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Transition Settings */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('transitionSettings')}>
          {expandedSections.transitionSettings ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span>TRANSITION SETTINGS</span>
        </div>
        {expandedSections.transitionSettings && (
          <div className={styles.sectionContent}>
            <div className={styles.field}>
              <label>Output Key</label>
              <input
                type="text"
                value={node.data.outputKey || ''}
                onChange={(e) => updateNodeData('outputKey', e.target.value)}
                placeholder="e.g.: analysis_result"
              />
            </div>
            {/* Same toggles as assistant */}
          </div>
        )}
      </div>

      {renderRetryPolicySection()}
    </>
  );

  // Custom/Transform configuration
  const renderCustomConfig = () => (
    <>
      <div className={styles.field}>
        <div className={styles.fieldHeader}>
          <label>Task</label>
          <button className={styles.expandButton}>
            <Maximize2 size={14} />
            Expand
          </button>
        </div>
        <textarea
          value={node.data.task || ''}
          onChange={(e) => updateNodeData('task', e.target.value)}
          placeholder="Describe the task for this node"
          rows={4}
        />
      </div>

      <div className={styles.field}>
        <div className={styles.fieldHeader}>
          <label>Output Schema</label>
          <button className={styles.expandButton}>
            <Maximize2 size={14} />
            Expand
          </button>
        </div>
        <textarea
          value={node.data.outputSchema || ''}
          onChange={(e) => updateNodeData('outputSchema', e.target.value)}
          placeholder="Expected output format (JSON Schema)"
          rows={3}
        />
      </div>

      <div className={styles.toggleField}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={node.data.interruptBefore ?? false}
            onChange={(e) => updateNodeData('interruptBefore', e.target.checked)}
          />
          <span className={styles.toggleSlider}></span>
        </label>
        <span>Interrupt Before</span>
        <button className={styles.infoButton}><Info size={14} /></button>
      </div>

      <div className={styles.toggleField}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={node.data.finishIteration ?? false}
            onChange={(e) => updateNodeData('finishIteration', e.target.checked)}
          />
          <span className={styles.toggleSlider}></span>
        </label>
        <span>Finish Iteration</span>
        <button className={styles.infoButton}><Info size={14} /></button>
      </div>

      <div className={styles.toggleField}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={node.data.resolveDynamicValues ?? false}
            onChange={(e) => updateNodeData('resolveDynamicValues', e.target.checked)}
          />
          <span className={styles.toggleSlider}></span>
        </label>
        <span>Resolve Dynamic Values in Prompt</span>
        <button className={styles.infoButton}><Info size={14} /></button>
      </div>

      <div className={styles.toggleField}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={node.data.resultAsHumanMessage ?? false}
            onChange={(e) => updateNodeData('resultAsHumanMessage', e.target.checked)}
          />
          <span className={styles.toggleSlider}></span>
        </label>
        <span>Result as Human Message</span>
        <button className={styles.infoButton}><Info size={14} /></button>
      </div>

      {/* Transition Settings */}
      <div className={styles.section}>
        <div className={styles.sectionHeader} onClick={() => toggleSection('transitionSettings')}>
          {expandedSections.transitionSettings ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span>TRANSITION SETTINGS</span>
        </div>
        {expandedSections.transitionSettings && (
          <div className={styles.sectionContent}>
            {/* Same as assistant transition settings */}
          </div>
        )}
      </div>

      {renderRetryPolicySection()}
    </>
  );

  // Note configuration
  const renderNoteConfig = () => (
    <div className={styles.field}>
      <label>Note Content</label>
      <textarea
        value={node.data.content || ''}
        onChange={(e) => updateNodeData('content', e.target.value)}
        placeholder="Add documentation or notes here..."
        rows={6}
      />
    </div>
  );

  // Basic configuration
  const renderBasicConfig = () => (
    <div className={styles.field}>
      <label>Label</label>
      <input
        type="text"
        value={node.data.label || ''}
        onChange={(e) => updateNodeData('label', e.target.value)}
      />
    </div>
  );

  // Retry Policy Section (reusable)
  const renderRetryPolicySection = () => (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={() => toggleSection('retryPolicy')}>
        {expandedSections.retryPolicy ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>RETRY POLICY</span>
      </div>
      {expandedSections.retryPolicy && (
        <div className={styles.sectionContent}>
          <div className={styles.fieldRow}>
            <label>Initial (s):</label>
            <input
              type="number"
              value={node.data.retryInitial ?? 1}
              onChange={(e) => updateNodeData('retryInitial', parseInt(e.target.value))}
            />
          </div>
          <div className={styles.fieldRow}>
            <label>Backoff:</label>
            <input
              type="number"
              value={node.data.retryBackoff ?? 2}
              onChange={(e) => updateNodeData('retryBackoff', parseInt(e.target.value))}
            />
          </div>
          <div className={styles.fieldRow}>
            <label>Max (s):</label>
            <input
              type="number"
              value={node.data.retryMax ?? 60}
              onChange={(e) => updateNodeData('retryMax', parseInt(e.target.value))}
            />
          </div>
          <div className={styles.fieldRow}>
            <label>Attempts:</label>
            <input
              type="number"
              value={node.data.retryAttempts ?? 3}
              onChange={(e) => updateNodeData('retryAttempts', parseInt(e.target.value))}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Tool Selection Modal
  const renderToolModal = () => {
    if (!showToolModal) return null;

    const toolCategories = [
      { id: 'access-management', name: 'Access Management', icon: '00' },
      { id: 'azure-devops-test-plan', name: 'Azure DevOps Test Plan', icon: '00' },
      { id: 'azure-devops-wiki', name: 'Azure DevOps Wiki', icon: '00' },
      { id: 'azure-devops-work-item', name: 'Azure DevOps Work Item', icon: '00' },
      { id: 'cloud', name: 'Cloud', icon: '00' },
      { id: 'codebase-tools', name: 'Codebase Tools', icon: '</>' },
      { id: 'project-management', name: 'Project Management', icon: '≡' },
      { id: 'vcs', name: 'VCS', icon: '⊢' },
    ];

    return (
      <div className={styles.modalOverlay} onClick={() => setShowToolModal(false)}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>Select Tool</h3>
            <button className={styles.modalClose} onClick={() => setShowToolModal(false)}>
              <X size={18} />
            </button>
          </div>
          <div className={styles.modalContent}>
            <div className={styles.toolSection}>
              <div className={styles.toolSectionHeader}>
                <span>Available Tools</span>
                <ChevronDown size={16} />
              </div>
              <div className={styles.toolList}>
                {toolCategories.map(category => (
                  <div key={category.id} className={styles.toolItem}>
                    <div className={styles.toolItemContent}>
                      <span className={styles.toolIcon}>{category.icon}</span>
                      <div className={styles.toolInfo}>
                        <span className={styles.toolName}>{category.name}</span>
                        <span className={styles.toolStatus}>Not selected</span>
                      </div>
                    </div>
                    <ChevronDown size={16} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.cancelButton} onClick={() => setShowToolModal(false)}>
              Cancel
            </button>
            <button className={styles.saveButton} onClick={() => setShowToolModal(false)}>
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const isStartOrEnd = node.type === 'start' || node.type === 'end';

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h3>Node configuration</h3>
          <button className={styles.collapseToggle}>
            <ChevronDown size={16} />
          </button>
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className={styles.content}>
        {/* Node Description */}
        {getNodeDescription() && (
          <p className={styles.description}>{getNodeDescription()}</p>
        )}

        {/* Node-specific configuration */}
        {renderNodeConfig()}
      </div>

      {/* Footer Actions */}
      {!isStartOrEnd && (
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button className={styles.deleteButton} onClick={() => onDelete(node.id)}>
              <Trash2 size={14} />
              Delete
            </button>
            <button className={styles.duplicateButton} onClick={() => onDuplicate(node.id)}>
              <Copy size={14} />
              Duplicate
            </button>
          </div>
          <div className={styles.footerRight}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button className={styles.saveButton} onClick={onClose}>
              Save
            </button>
          </div>
        </div>
      )}

      {/* Tool Selection Modal */}
      {renderToolModal()}
    </div>
  );
};

export default NodeConfigPanel;
